import * as React from 'react';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';

// REDUX
import { useAppSelector } from '../../store';

// COMPONENTS
import Button from '../../components/Button';
import DateTimeInput from '../../components/DateTimeInput';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import SingleSelect from '../../components/SingleSelect';
import FileUpload from '../../components/FileUpload';

// ICONS
import { ReactComponent as QuestionMarkIcon } from '../../assets/svg/questionmark.svg';

// TYPES
import { FeedbackFormTypes } from './FeedbackSubmit';

interface StepOneTypes {
	feedbackFormData: FeedbackFormTypes;
	setFeedbackFormData: React.Dispatch<React.SetStateAction<FeedbackFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;
}

const StepOne: React.FC<StepOneTypes> = ({ feedbackFormData, setFeedbackFormData, setActiveStep }) => {
	const feedbackCategories = useAppSelector((state) => state.lists.feedbackCategories);

	const onSubmitStepOne = (values: any) => {
		setActiveStep('step2');

		window.scrollTo(0, 0);

		setFeedbackFormData((prevState) => ({
			...prevState,

			subject: values.subjectInput,
			occurrence: values.occurrenceInput,
			category: values.categoryInput,
			summary: values.summaryInput,
			anonymously: values.anonymouslyInput,
			anonymousContact: values.anonymousContactInput,
			isResponseRequired: values.isResponseRequiredInput,
			createAttachments: values.createAttachments,
			attachments: values.attachments
		}));
	};

	const validationSchemaStepOne = Yup.object({
		subjectInput: Yup.string().required('Required'),
		occurrenceInput: Yup.string().required('Required'),
		categoryInput: Yup.string().required('Required'),
		summaryInput: Yup.string().required('Required'),
		anonymouslyInput: Yup.boolean().required('Required'),
		isResponseRequiredInput: Yup.boolean().required('Required')
	});

	const initialValuesStepOne = {
		subjectInput: feedbackFormData.subject,
		occurrenceInput: feedbackFormData.occurrence,
		categoryInput: feedbackFormData.category,
		summaryInput: feedbackFormData.summary,
		anonymouslyInput: feedbackFormData.anonymously,
		anonymousContactInput: feedbackFormData.anonymousContact,
		isResponseRequiredInput: feedbackFormData.isResponseRequired,
		createAttachments: feedbackFormData.createAttachments,
		attachments: feedbackFormData.attachments
	};

	return (
		<Formik
			initialValues={initialValuesStepOne}
			validationSchema={validationSchemaStepOne}
			onSubmit={onSubmitStepOne}
			enableReinitialize
		>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						<div className="general-form-layout-wrapper">
							<div className="form-main">
								<div className="form-group">
									<label htmlFor="subjectInput" className="label">
										Subject
									</label>

									<div className="form-field">
										<Field
											name="subjectInput"
											id="subjectInput"
											type="text"
											placeholder="eg. broken play equipment"
											className="form-input"
										/>

										<ErrorMessage name="subjectInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label className="label">Date and time of relevant event</label>

									<DateTimeInput
										dateTimeString={formik.values.occurrenceInput || ''}
										onChange={(val: string) => formik.setFieldValue('occurrenceInput', val)}
									/>
								</div>

								<div className="form-group">
									<label htmlFor="categoryInput" className="label">
										Feedback Type
									</label>

									<SingleSelect
										options={feedbackCategories}
										name="categoryInput"
										value={formik.values.categoryInput}
										onChange={(val: string) => formik.setFieldValue('categoryInput', val)}
									/>

									<ErrorMessage name="categoryInput" component="div" className="error-message" />
								</div>

								<div className="form-group">
									<label htmlFor="summaryInput" className="label">
										Please detail your feedback
									</label>

									<div className="form-field">
										<Field as="textarea" name="summaryInput" id="summaryInput" type="text" className="form-input" />
										<ErrorMessage name="summaryInput" component="div" className="error-message" />
									</div>
								</div>

								<div className="form-group">
									<label htmlFor="createAttachmentsInput" className="label">
										Add attachment
									</label>

									<div className="form-field mbtom-14">
										<FileUpload
											name="createAttachments"
											maxSizeMb={5}
											multiple={true}
											onChange={(vals: string[], files: any[]) => {
												formik.setFieldValue('createAttachments', vals);
												formik.setFieldValue('attachments', files);
											}}
											files={formik.values.attachments}
										/>
									</div>
								</div>

								<div className="form-group">
									<label className="label">Do you wish to submit this feedback anonymously?</label>
									<div className="form-field">
										<div className="radio-row">
											<Field
												name="anonymouslyInput"
												id="anonymouslyInput"
												type="radio"
												value={true}
												checked={formik.values.anonymouslyInput === true}
												onChange={() => formik.setFieldValue('anonymouslyInput', true)}
											/>
											<span className="radio-option">Yes</span>
										</div>

										<div className="radio-row">
											<Field
												name="anonymouslyInput"
												id="anonymouslyInput"
												type="radio"
												value={false}
												checked={formik.values.anonymouslyInput === false}
												onChange={() => formik.setFieldValue('anonymouslyInput', false)}
											/>
											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="anonymouslyInput" component="div" className="error-message" />

										{formik.values.anonymouslyInput === true && (
											<div className="form-field">
												<Field
													name="anonymousContactInput"
													id="anonymousContactInput"
													type="text"
													className="form-input sub-input"
													placeholder="Contact email or phone number (optional)"
												/>

												<ErrorMessage
													name="anonymousContactInput"
													component="div"
													className="error-message sub-input"
												/>
											</div>
										)}
									</div>
								</div>

								<div className="form-group">
									<label className="label">Do you want a response from the organisation?</label>
									<div className="form-field">
										<div className="radio-row">
											<Field
												name="isResponseRequiredInput"
												id="isResponseRequiredInput"
												type="radio"
												value={true}
												checked={formik.values.isResponseRequiredInput === true}
												onChange={() => formik.setFieldValue('isResponseRequiredInput', true)}
											/>
											<span className="radio-option">Yes</span>
										</div>
										<div className="radio-row">
											<Field
												name="isResponseRequiredInput"
												id="isResponseRequiredInput"
												type="radio"
												value={false}
												checked={formik.values.isResponseRequiredInput === false}
												onChange={() => formik.setFieldValue('isResponseRequiredInput', false)}
											/>
											<span className="radio-option">No</span>
										</div>

										<ErrorMessage name="isResponseRequiredInput" component="div" className="error-message" />
									</div>
								</div>
							</div>

							<div className="general-form-guide">
								<QuestionMarkIcon />
								<h2 className="guide-heading">When to submit Feedback</h2>

								<p className="guide-content">
									Feedback is an important way for organisations to improve safety practices. You may have observed a
									safety risk or have a suggestion about what could be done better to improve children’s safety.
									ChildAware organisations welcome feedback as part of their commitment to continuous improvement. If
									your concern is of a serious nature or relates to a specific incident, it may be appropriate to submit
									a&nbsp;
									<Link className="link" to="/complaints-submit">
										Complaint
									</Link>
									&nbsp; instead.
								</p>

								<p className="guide-content">
									<strong>Note:</strong> If a child is in immediate danger, call police or emergency services on 000.
								</p>
							</div>
						</div>

						<ScrollToTopButton />

						<div className="general-divider"></div>

						<div className="form-button-group">
							<Button type="submit" className="btn-primary submit-btn complaints-submit-page">
								Continue
							</Button>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};

export default StepOne;
